import { cn } from "@/lib/utils";

const sizes = { sm: 20, md: 28, lg: 40 } as const;

interface Props {
  sport?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export default function SportIcon({ sport = "padel", size = "md", className }: Props) {
  const s = sizes[size];
  const shared = {
    width: s,
    height: s,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className: cn(className),
  };

  switch (sport) {
    case "padel":
      return (
        <svg {...shared}>
          {/* Rounded-rect head (padel-specific shape) */}
          <rect x="5" y="1.5" width="14" height="15" rx="5" ry="5" />
          {/* Handle */}
          <line x1="12" y1="16.5" x2="12" y2="22.5" />
          {/* Grip wrap */}
          <line x1="10.5" y1="21.5" x2="13.5" y2="21.5" />
          {/* Hole pattern — 3×3 grid, clearly visible */}
          <circle cx="9" cy="5.5" r="1" fill="none" />
          <circle cx="12" cy="5.5" r="1" fill="none" />
          <circle cx="15" cy="5.5" r="1" fill="none" />
          <circle cx="9" cy="9" r="1" fill="none" />
          <circle cx="12" cy="9" r="1" fill="none" />
          <circle cx="15" cy="9" r="1" fill="none" />
          <circle cx="9" cy="12.5" r="1" fill="none" />
          <circle cx="12" cy="12.5" r="1" fill="none" />
          <circle cx="15" cy="12.5" r="1" fill="none" />
        </svg>
      );

    case "tennis":
      return (
        <svg {...shared}>
          <ellipse cx="12" cy="9" rx="6.5" ry="7.5" />
          <line x1="12" y1="16.5" x2="12" y2="23" />
          <line x1="10" y1="22" x2="14" y2="22" />
          {/* String pattern */}
          <line x1="7" y1="9" x2="17" y2="9" strokeWidth="0.8" />
          <line x1="8" y1="6" x2="16" y2="6" strokeWidth="0.8" />
          <line x1="8" y1="12" x2="16" y2="12" strokeWidth="0.8" />
          <line x1="12" y1="1.5" x2="12" y2="16" strokeWidth="0.8" />
          <line x1="9.5" y1="2" x2="9.5" y2="15.5" strokeWidth="0.8" />
          <line x1="14.5" y1="2" x2="14.5" y2="15.5" strokeWidth="0.8" />
        </svg>
      );

    case "golf":
      return (
        <svg {...shared}>
          {/* Club */}
          <line x1="10" y1="2" x2="10" y2="20" />
          <path d="M10 2 L16 5 L10 6" />
          {/* Flag */}
          <line x1="10" y1="20" x2="6" y2="22" />
          <line x1="10" y1="20" x2="14" y2="22" />
          {/* Ball */}
          <circle cx="18" cy="20" r="2.2" />
        </svg>
      );

    case "badminton":
      return (
        <svg {...shared}>
          {/* Shuttlecock cork */}
          <ellipse cx="12" cy="7" rx="3.5" ry="3" />
          {/* Feathers */}
          <path d="M8.5 9 L5 20" />
          <path d="M15.5 9 L19 20" />
          <path d="M10 10 L8 20" />
          <path d="M14 10 L16 20" />
          <path d="M12 10 L12 20" />
          {/* Base curve */}
          <path d="M5 20 Q12 23 19 20" />
        </svg>
      );

    case "squash":
      return (
        <svg {...shared}>
          {/* Teardrop-shaped head */}
          <path d="M12 2 C7 2 5 6 5 10 C5 14 8 16 12 16 C16 16 19 14 19 10 C19 6 17 2 12 2Z" />
          {/* Handle */}
          <line x1="12" y1="16" x2="12" y2="23" />
          <line x1="10" y1="22" x2="14" y2="22" />
          {/* Strings */}
          <line x1="8" y1="8" x2="16" y2="8" strokeWidth="0.7" />
          <line x1="7.5" y1="11" x2="16.5" y2="11" strokeWidth="0.7" />
          <line x1="10" y1="4" x2="10" y2="15" strokeWidth="0.7" />
          <line x1="14" y1="4" x2="14" y2="15" strokeWidth="0.7" />
        </svg>
      );

    case "pickleball":
      return (
        <svg {...shared}>
          {/* Round paddle */}
          <circle cx="12" cy="9" r="7" />
          {/* Handle */}
          <line x1="12" y1="16" x2="12" y2="23" />
          <line x1="10" y1="22" x2="14" y2="22" />
          {/* Holes */}
          <circle cx="10" cy="6" r="0.7" fill="currentColor" stroke="none" />
          <circle cx="14" cy="6" r="0.7" fill="currentColor" stroke="none" />
          <circle cx="12" cy="8" r="0.7" fill="currentColor" stroke="none" />
          <circle cx="10" cy="10" r="0.7" fill="currentColor" stroke="none" />
          <circle cx="14" cy="10" r="0.7" fill="currentColor" stroke="none" />
          <circle cx="12" cy="12" r="0.7" fill="currentColor" stroke="none" />
          <circle cx="10" cy="14" r="0.7" fill="currentColor" stroke="none" />
          <circle cx="14" cy="14" r="0.7" fill="currentColor" stroke="none" />
          {/* Ball */}
          <circle cx="20" cy="18" r="2" />
        </svg>
      );

    default: // generic ball
      return (
        <svg {...shared}>
          <circle cx="12" cy="12" r="8" />
          <path d="M5 12 Q12 8 19 12" strokeWidth="0.8" />
          <path d="M5 12 Q12 16 19 12" strokeWidth="0.8" />
        </svg>
      );
  }
}
